import { createSelector } from '@reduxjs/toolkit';
import { isNumber } from 'lodash-es';

import { isInteger } from '@iconik-io/core/src/is_integer';
import { Timecode } from '@iconik-io/core/src/timecode';
import { truthyPredicate } from '@iconik-io/core/src/truthy_predicate';
import type {
  FilesComponentSchema,
  FilesFileSchemaWithId,
  FilesFileSchemaWithIdNullable,
  FilesFormatSchemaWithId,
  FilesFormatSchemaWithIdNullable,
  FilesProxySchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';
import type { ContentType } from '@iconik-io/unified-player-core';

import { selectAssetOrSearchItem } from '../../../../redux/entities/selectors/assets';
import { selectOriginalFilesByVersionIdMap } from '../../../../redux/entities/selectors/files';
import {
  selectAssetOriginalFormat,
  selectOriginalFormatsByVersionIdMap,
} from '../../../../redux/entities/selectors/formats';
import { selectKeyframesByVersionIdMap } from '../../../../redux/entities/selectors/keyframes';
import {
  getDefaultProxy,
  selectClosedProxiesVersionMapByAssetId,
  selectProxiesByAssetId,
} from '../../../../redux/entities/selectors/proxies';
import {
  selectAssetVersionExtended,
  selectAssetVersionsExtended,
  selectCurrentVersionId,
  selectLatestActiveVersionId,
} from '../../../../redux/entities/selectors/versions';
import type { BaseAppState } from '../../../../redux/types';
import { zeroTimecode } from '../../../../utils/constants/timecode';
import { filterExtendedVersionsBySelectedVersionIds } from '../../../../utils/filter_versions_by_selected_version_ids';
import { findBestKeyframe } from '../../../../utils/find_best_keyframe';
import { getOriginalFormatMediaType } from '../../../../utils/get_original_format_media_type';
import { getProxyMediaType } from '../../../../utils/get_proxy_media_type';
import { getProxyTimecodeOptions } from '../../../../utils/get_proxy_timecode_options';
import { isPdfFormat } from '../../../../utils/is_pdf_format';
import { isPdfProxyFormat } from '../../../../utils/is_pdf_proxy_format';
import {
  MEDIA_TYPE_AUDIO,
  MEDIA_TYPE_IMAGE,
  MEDIA_TYPE_PDF,
  MEDIA_TYPE_STREAM,
  MEDIA_TYPE_VIDEO,
} from '../../../../utils/media_type';
import { resolveMediaUrl } from '../../../../utils/resolve_media_url';
import type { LazyItem } from '../../shared/types';
import type { AppPlaylistItem } from './context';

const getProxy = (proxy: FilesProxySchemaWithId | undefined) => proxy;

const getProxySize = (proxy: FilesProxySchemaWithId) => {
  const { width, height } = proxy.resolution ?? {};
  const size =
    width && height
      ? {
          height,
          width,
        }
      : undefined;

  return size;
};

const getOriginalFormat = (
  _proxy: FilesProxySchemaWithId | undefined,
  originalFormat:
    | FilesFormatSchemaWithId
    | FilesFormatSchemaWithIdNullable
    | undefined,
) => originalFormat;

export const selectPlayerContentType = createSelector(
  [getProxy, getOriginalFormat],
  (proxy, originalFormat): ContentType => {
    if (isPdfFormat(originalFormat?.metadata ?? undefined)) {
      return 'pdf';
    }
    if (proxy?.format && isPdfProxyFormat(proxy.format)) {
      return 'pdf';
    }
    const originalMediaType = getOriginalFormatMediaType(
      originalFormat ?? undefined,
    );
    const mediaType = getProxyMediaType(proxy) ?? originalMediaType;

    // A watermarked share rewrites an image proxy's URL to the /dash/
    // manifest endpoint, which classifies as a stream; images are never
    // real streams, so let the original format win
    if (
      mediaType === MEDIA_TYPE_STREAM &&
      originalMediaType === MEDIA_TYPE_IMAGE
    ) {
      return 'image';
    }

    switch (mediaType) {
      case MEDIA_TYPE_VIDEO:
        return 'video';
      case MEDIA_TYPE_IMAGE:
        return 'image';
      case MEDIA_TYPE_AUDIO:
        return 'audio';
      case MEDIA_TYPE_STREAM:
        return 'stream';
      default:
        return 'unknown';
    }
  },
);

const getAssetId = (_state: BaseAppState, assetId: string) => assetId;

export function getPlaylistItemVariants(
  proxies: FilesProxySchemaWithId[] | undefined,
  originalFile:
    | FilesFileSchemaWithId
    | FilesFileSchemaWithIdNullable
    | undefined,
): AppPlaylistItem['availableVariants'] {
  if (!proxies) {
    return [];
  }

  return proxies.map((proxy) => ({
    type: proxy.is_multichannel ? 'multi_channel_audio_proxy' : 'proxy',
    audioChannels: proxy.audio_channels ?? undefined,
    url: resolveMediaUrl(
      proxy,
      originalFile,
      selectPlayerContentType(proxy, undefined),
    ),
  }));
}

const getPlaylistItemId = (
  _state: BaseAppState,
  _assetId: string,
  _versionId: string | undefined,
  playlistItemId: string,
) => playlistItemId;

export const selectSubclipUsesCustomProxy = createSelector(
  selectAssetOrSearchItem,
  selectLatestActiveVersionId,
  selectProxiesByAssetId,
  (asset, versionId, proxies) => {
    if (asset?.type === 'SUBCLIP') {
      const hasNonParentProxies = proxies.some((proxy) =>
        versionId ? proxy.version_id === versionId : true,
      );

      return hasNonParentProxies;
    }
    return false;
  },
);

const selectPlaylistItem = createSelector(
  getAssetId,
  selectCurrentVersionId,
  getPlaylistItemId,
  selectAssetOrSearchItem,
  selectClosedProxiesVersionMapByAssetId,
  selectKeyframesByVersionIdMap,
  selectOriginalFilesByVersionIdMap,
  selectOriginalFormatsByVersionIdMap,
  selectSubclipUsesCustomProxy,
  (
    assetId,
    versionId,
    playlistItemId,
    asset,
    proxiesByVersionId,
    keyframesByVersionIdMap,
    originalFilesByVersionIdMap,
    originalFormatsByVersionIdMap,
    subclipUsesCustomProxy,
  ) => {
    let proxies = versionId ? proxiesByVersionId.get(versionId) : undefined;

    // Proxy might not exist by specified version ID, we also try to fetch it
    // by original version ID which is also a fallback for a proxy in some cases.
    if (!proxies) {
      proxies = asset?.original_version_id
        ? proxiesByVersionId.get(asset.original_version_id)
        : undefined;
    }

    const proxy = getDefaultProxy(proxies);

    const originalFormat = versionId
      ? originalFormatsByVersionIdMap[versionId]
      : undefined;

    // don't set a dummy playlist item for pdfs
    // password-protected won't have a proxy
    const isPdf = isPdfFormat(originalFormat?.metadata);
    if ((!asset || !proxy) && !isPdf) {
      const playerItem: AppPlaylistItem = {
        id: playlistItemId,
        contentType: 'unknown',
        url: undefined,
        name: '',
        meta: {
          assetId,
          versionId,
        },
      };
      return playerItem;
    }
    const contentType = selectPlayerContentType(proxy, originalFormat);
    const timecodeOptions = proxy ? getProxyTimecodeOptions(proxy) : undefined;

    const formatComponents = originalFormat?.components ?? [];
    const durationItems = formatComponents
      ?.filter((item) => item.metadata?.duration)
      .map(
        (component: FilesComponentSchema) =>
          Number(component.metadata?.duration) / 1000,
      );

    const duration =
      durationItems.length > 0 ? Math.max(...durationItems) : undefined;

    const naturalSize = proxy ? getProxySize(proxy) : undefined;

    const thumbnailUrl = versionId
      ? findBestKeyframe(keyframesByVersionIdMap[versionId])?.url
      : undefined;

    const originalFile = versionId
      ? originalFilesByVersionIdMap[versionId]
      : undefined;

    const url = resolveMediaUrl(proxy, originalFile, contentType);

    // The PDF original file can be missing or inaccessible (e.g. deleted, or
    // the share disallows downloads). Expose the image proxy URL as a fallback
    // candidate; the consumer decides when to apply it. The decision must not
    // live here: it depends on fetch-settled state, and gating on it would
    // destabilize this selector's inputs during version switches.
    // TODO(FE-7071): Remove once PDFs are rendered from proxies only and
    // originals are not loaded for viewing.
    let pdfFallbackImageUrl: string | undefined;
    if (
      contentType === MEDIA_TYPE_PDF &&
      url === undefined &&
      getProxyMediaType(proxy) === MEDIA_TYPE_IMAGE
    ) {
      pdfFallbackImageUrl = resolveMediaUrl(proxy, undefined, MEDIA_TYPE_IMAGE);
    }

    let playableRange;
    if (
      asset?.type === 'SUBCLIP' &&
      isInteger(asset.time_start_milliseconds) &&
      isInteger(asset.time_end_milliseconds) &&
      !subclipUsesCustomProxy
    ) {
      playableRange = {
        start: asset.time_start_milliseconds / 1000,
        end: asset.time_end_milliseconds / 1000,
      };
    }

    let firstFrameTimecode = timecodeOptions?.startTimecode;

    if (
      subclipUsesCustomProxy &&
      isNumber(asset?.time_start_milliseconds) &&
      timecodeOptions
    ) {
      const startTimecode = new Timecode(timecodeOptions, {
        smpte: proxy?.start_time_code ?? zeroTimecode,
      });

      const timecode = new Timecode(timecodeOptions, {
        seconds:
          startTimecode.toSeconds() + asset.time_start_milliseconds / 1000,
      });

      firstFrameTimecode = timecode.toSMPTE();
    }

    const playlistItem: AppPlaylistItem = {
      id: playlistItemId,
      contentType,
      url,
      name: asset?.title ?? '',
      frameRate: timecodeOptions?.frameRate ?? 0,
      isDropFrame: timecodeOptions?.isDropFrame ?? false,
      duration,
      naturalSize,
      thumbnailUrl: thumbnailUrl ?? undefined,
      playableRange,
      firstFrameTimecode,
      availableVariants: getPlaylistItemVariants(proxies, originalFile),
      meta: {
        assetId,
        versionId,
        pdfFallbackImageUrl,
      },
    };

    return playlistItem;
  },
);

export const selectSelectedVersionIdList = (
  _state: BaseAppState,
  _assetId: string | undefined,
  _versionId: string | undefined,
  selectedVersionIdList?: string[] | undefined,
) => selectedVersionIdList;

export const selectComparisonItems = createSelector(
  selectAssetOrSearchItem,
  selectClosedProxiesVersionMapByAssetId,
  selectAssetOriginalFormat,
  selectKeyframesByVersionIdMap,
  selectOriginalFilesByVersionIdMap,
  selectAssetVersionExtended,
  selectAssetVersionsExtended,
  selectSelectedVersionIdList,
  (
    asset,
    proxyByVersionId,
    originalFormat,
    keyframesByVersionIdMap,
    filesByVersionIdMap,
    version,
    versions,
    selectedVersionIdList,
  ): AppPlaylistItem[] => {
    const activeVersions = versions
      .toReversed()
      .filter((item) => item.status === 'ACTIVE');
    const filteredVersion = selectedVersionIdList
      ? filterExtendedVersionsBySelectedVersionIds(
          activeVersions,
          selectedVersionIdList,
        )
      : activeVersions;
    const comparisonItems = filteredVersion
      .map((versionItem) => {
        const versionProxies = proxyByVersionId.get(versionItem.id);
        const versionProxy = getDefaultProxy(versionProxies);
        if (!asset) {
          return undefined;
        }
        if (!version) {
          return undefined;
        }
        if (versionItem.id === version.id) {
          return undefined;
        }

        if (
          (!asset || !versionProxy) &&
          !isPdfFormat(originalFormat?.metadata)
        ) {
          const playerItem: AppPlaylistItem = {
            id: versionItem.id,
            contentType: 'unknown',
            url: undefined,
            name: '',
            meta: {
              assetId: asset.id,
              versionId: versionItem.id,
            },
          };
          return playerItem;
        }

        const versionContentType = selectPlayerContentType(
          versionProxy,
          originalFormat,
        );

        const thumbnailUrl =
          findBestKeyframe(keyframesByVersionIdMap[versionItem.id])?.url ??
          undefined;

        const timecodeOptions = versionProxy
          ? getProxyTimecodeOptions(versionProxy)
          : undefined;

        const url = resolveMediaUrl(
          versionProxy,
          filesByVersionIdMap[versionItem.id],
          versionContentType,
        );

        const playerItem: AppPlaylistItem = {
          id: versionItem.id,
          contentType: versionContentType,
          url,
          name: `${asset.title} ${versionItem.name}`,
          thumbnailUrl,
          frameRate: timecodeOptions?.frameRate ?? 0,
          meta: {
            assetId: asset.id,
            versionId: versionItem.id,
          },
          availableVariants: getPlaylistItemVariants(
            versionProxies,
            filesByVersionIdMap[versionItem.id],
          ),
        };

        return playerItem;
      })
      .filter(truthyPredicate);

    return comparisonItems;
  },
);

function createPlaylistSelector() {
  // These variables are held in the closure, acting as a cache.
  let lastItemsArray: AppPlaylistItem[] = [];
  let lastResultPlaylist: Record<string, AppPlaylistItem> = {};

  return (
    state: BaseAppState,
    items: LazyItem[],
    versionsMap: Record<string, string | undefined>,
  ): Record<string, AppPlaylistItem> => {
    const newItemsArray = items
      .map((item) =>
        selectPlaylistItem(
          state,
          item.objectId,
          versionsMap[item.objectId] ?? item.versionId,
          item.id,
        ),
      )
      .filter(truthyPredicate);

    let hasChanged = false;
    if (newItemsArray.length !== lastItemsArray.length) {
      hasChanged = true;
    } else {
      for (let i = 0; i < newItemsArray.length; i++) {
        if (newItemsArray[i] !== lastItemsArray[i]) {
          hasChanged = true;
          break;
        }
      }
    }

    if (hasChanged) {
      const newPlaylist = Object.fromEntries(
        newItemsArray.map((item) => [item.id, item]),
      );

      lastItemsArray = newItemsArray;
      lastResultPlaylist = newPlaylist;

      return newPlaylist;
    }

    return lastResultPlaylist;
  };
}

export const selectPlaylist = createPlaylistSelector();
